import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { useSelector, useDispatch } from "react-redux";
import { markAdminNotificationRead } from "../../actions/adminNotificationActions";
import { formatNotificationCount, getAdminNotificationHref, getNotificationId } from "../../utils/adminNotificationUtils";
import AdminNotificationPanel from "./AdminNotificationsPanel";
import AdminNotificationToast from "./AdminNotificationToast";

const AdminNotificationBell = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const auth = useSelector((state) => state.auth);
    const { unreadCount = 0, countsError = "" } = useSelector((state) => state.notification);
    const [open, setOpen] = useState(false);
    const [toastList, setToastList] = useState([]);
    const canUse = auth.authenticate && ["admin", "superAdmin"].includes(auth.user?.role);
    const ownerId = getNotificationId(auth.user);

    useEffect(() => {
        setOpen(false);
        setToastList([]);
    }, [canUse, ownerId]);

    useEffect(() => {
        if (open) setToastList([]);
    }, [open]);

    const removeToast = useCallback((id) => {
        setToastList((items) => items.filter((item) => getNotificationId(item) !== id));
    }, []);

    const openObject = useCallback((data) => {
        const href = getAdminNotificationHref(data, auth.user);
        if (href) {
            setOpen(false);
            navigate(href);
        } else setOpen(true);
        removeToast(getNotificationId(data));
    }, [auth.user, navigate, removeToast]);

    const openToast = useCallback(async (data) => {
        const ok = data.isRead || await dispatch(markAdminNotificationRead(getNotificationId(data)));
        if (ok) openObject(data);
        else setOpen(true);
    }, [dispatch, openObject]);

    useEffect(() => {
        if (!canUse) return undefined;
        const onToast = (event) => {
            if (open) return;
            const data = event.detail;
            const id = getNotificationId(data);
            if (!id) return;
            setToastList((items) => [...items.filter((item) => getNotificationId(item) !== id), data].slice(-3));
        };
        const onOpen = (event) => openToast(event.detail);
        window.addEventListener("admin-toast", onToast);
        window.addEventListener("admin-notification:open", onOpen);
        return () => {
            window.removeEventListener("admin-toast", onToast);
            window.removeEventListener("admin-notification:open", onOpen);
        };
    }, [canUse, open, openToast]);

    if (!canUse) return null;

    return (
        <>
            <button
                type="button"
                className={"admin-notification-bell" + (open ? " is-open" : "")}
                onClick={() => setOpen((value) => !value)}
                aria-label={"Уведомления. Непрочитанных: " + unreadCount}
                aria-expanded={open}
                aria-controls="admin-notifications-panel"
                title={countsError || "Уведомления"}
            >
                <NotificationsIcon fontSize="small" />
                <span className="admin-notification-bell__label">Уведомления</span>
                {unreadCount > 0 && <b className="admin-notification-badge">{formatNotificationCount(unreadCount)}</b>}
                {countsError && <span className="admin-notification-bell__error" aria-label="Ошибка обновления счётчика">!</span>}
            </button>
            <AdminNotificationPanel open={open} setOpen={setOpen} onOpenNotification={openObject} />
            {!open && toastList.length > 0 && createPortal(
                <div className="admin-notification-toast-stack" aria-live="polite">
                    {toastList.map((data) => (
                        <AdminNotificationToast key={getNotificationId(data)} data={data} onClose={removeToast} onOpen={openToast} />
                    ))}
                </div>,
                document.body
            )}
        </>
    );
};

export default AdminNotificationBell;
